/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/errorDetail.ts
 *
 * Backend-Fehler lesen: genau zwei Helfer, und sonst niemand.
 *
 * Das Feld `detail` einer FastAPI-Fehlerantwort trägt zwei völlig verschiedene
 * Dinge — lokalisierte Prosa für Menschen und Maschinencodes für das Frontend
 * (`plan_missing:module.publish`, `plan_limit_reached:seats:current=3:limit=2`).
 * Wer es direkt rendert, zeigt früher oder später einem zahlenden Kunden einen
 * internen Code. Genau das ist am 26.07.2026 in 121suite passiert: die
 * Kampagnen-Seite zeigte „Alle Kampagnen · plan_missing:includes_module_paid",
 * weil fünf lokale Kopien desselben Helfers existierten und zehn Stellen
 * `response.data.detail` unmittelbar lasen. Und am 04.08.2026 wieder in
 * 121vendors: „plan_missing:module.assignment_send" stand als roter Balken über
 * der Auftragsliste, weil dort ein `apiErrorMessage` den Rohwert durchreichte.
 *
 * Deshalb:
 *
 *   extractErrorDetail(err)  →  was man ANZEIGEN darf. Maschinencodes und
 *                               unaufgelöste Übersetzungsschlüssel ergeben
 *                               `null`, damit jeder Aufruf mit
 *                               `?? t('errors.generic')` automatisch auf einen
 *                               echten Satz fällt.
 *   errorCode(err)           →  worauf man VERZWEIGEN darf.
 *
 * Niemand sonst liest `response.data.detail`. Keine lokalen Kopien — die
 * driften garantiert auseinander, und genau die Kopie ohne Schutz rendert dann
 * den Code. Ein CI-Wächter erzwingt das (`check-no-raw-errors.py`, liegt neben
 * dieser Datei); Ausnahme sind nur dieser Helfer selbst und Staff-/Admin-
 * Oberflächen, wo der Rohcode das nützlichste Signal ist.
 *
 * Kein Axios-Import: `paperball-*` benutzt `fetch`. Der Helfer arbeitet auf der
 * Form, nicht auf der Bibliothek — er akzeptiert einen Axios-Fehler, ein
 * geparstes Antwort-Objekt oder den Body selbst.
 */
/**
 * Ein Maschinencode hat einen snake_case-Kopf und darf nach dem ersten `:`
 * einen Anhang tragen (`plan_limit_reached:seats:current=3:limit=2`).
 *
 * Streng ist nur der Kopf. Der Anhang ist beliebig, solange er kein
 * Leerzeichen enthält: ein Satz hat Leerzeichen, ein Code nie. An dieser einen
 * Eigenschaft hängt die Unterscheidung — nicht an den Zeichen im Anhang.
 *
 * Der Anhang trägt nämlich oft einen Laufzeitwert, und der hält sich an kein
 * Zeichenset: `document:unsupported_type:application/pdf` (Schrägstrich),
 * `guardrail:blocked:pii,secret` (Komma), `rag:model_unknown:openai/gpt-4o`
 * oder `…:llama3:8b` (Modellnamen), `resource_not_found:paperball://doc/7`.
 * Bis 17.09.2026 liess das Muster im Anhang nur `[A-Za-z0-9_.=-]` zu. Solche
 * Codes galten deshalb als Satz — `errorCode` gab `null`, und
 * `extractErrorDetail` reichte den Rohcode an die Oberfläche durch
 * (paperball-ai, `apiErrorText`).
 *
 * Kopf und Anhang zusammen: ein Wort mit Großbuchstaben am Anfang
 * („Fehlgeschlagen") und alles mit Leerzeichen („fehler: bitte prüfen") bleibt
 * Prosa. Werte mit Leerzeichen (Freitext, `str(exc)`, Dateinamen) gehören
 * deshalb nicht in einen Code — sonst wird er als Satz angezeigt.
 */
const CODE_MUSTER = /^[a-z][a-z0-9_]*(?::\S+)?$/;
export function istMaschinencode(text) {
    return CODE_MUSTER.test(text.trim());
}
/**
 * Ein unaufgelöster Übersetzungsschlüssel (`errors.plan.missing.api_import`).
 *
 * Kein Maschinencode — auf ihn verzweigt niemand, `errorCode` liefert ihn
 * deshalb NICHT —, sondern ein Backend-Fehler: Der Schlüssel fehlt im
 * Wörterbuch, und der Auflöser gibt ihn roh heraus. Befund 17.09.2026 in
 * paperball-finance: „Das Wertpapier konnte nicht entfernt werden:
 * errors.maintenance" stand im Dialog, weil `CODE_MUSTER` am Punkt im Kopf
 * scheiterte und der Schlüssel als Satz galt.
 *
 * Bewusst getrennt von `CODE_MUSTER`: Das ist wörtlich die Code-Form des
 * Pre-commit-Hooks (`check_http_detail.py`), und dort ist ein literales
 * `detail="errors.x"` ein Fehler, kein erlaubter Code.
 *
 * Erkannt wird mindestens ein Punkt zwischen Bezeichnern, ohne Leerzeichen —
 * ein Satz hat Leerzeichen, ein Satzende hat nichts hinter dem Punkt.
 */
const SCHLUESSEL_MUSTER = /^[a-z][a-z0-9_]*(?:\.[A-Za-z0-9_]+)+$/;
export function istRoherSchluessel(text) {
    return SCHLUESSEL_MUSTER.test(text.trim());
}
/**
 * Darf dieser Text vor den Nutzer? Nein bei Code, bei rohem Schlüssel und bei
 * der englischen Standard-Phrase des Status (`istStatusPhrase`).
 * Der rohe Schlüssel geht laut ins Protokoll (Regel #7): Der Nutzer sieht den
 * Satz des Aufrufers, der Fehler im Wörterbuch bleibt sichtbar. Die
 * Status-Phrase nicht — sie ist kein Fehler im Backend, sondern Starlettes
 * Vorgabe, und der Status selbst bleibt dem Aufrufer über `errorStatus`.
 */
function anzeigbar(text, status) {
    if (istMaschinencode(text))
        return null;
    if (istStatusPhrase(text, status))
        return null;
    if (istRoherSchluessel(text)) {
        console.error('[errorDetail] unaufgelöster Übersetzungsschlüssel vom Backend:', text);
        return null;
    }
    return text;
}
/**
 * Die Standard-Begründung jedes HTTP-Status, wie Python sie in
 * `http.HTTPStatus(status).phrase` führt.
 *
 * Starlette/FastAPI schreibt genau diese Phrase in `detail`, wenn eine Route
 * keinen eigenen Satz mitgibt: `{"detail": "Not Found"}` für eine unbekannte
 * Route, „Method Not Allowed" (405), „Internal Server Error" (500) … Das ist
 * englischer Rahmenwerk-Text, keine Prosa für den Nutzer — er enthält aber
 * Leerzeichen und rutscht deshalb an `istMaschinencode` vorbei. Am 17.09.2026
 * stand in paperball-finance (PR #600) so „Speichern fehlgeschlagen: Not Found"
 * vor dem Nutzer.
 *
 * Verglichen wird gegen den STATUS der Antwort, nicht gegen eine Liste
 * verdächtiger Wörter: „Not Found" bei einem 404 ist die Starlette-Vorgabe,
 * derselbe Text bei einem 400 wäre ein bewusst gesetzter (wenn auch
 * schlechter) Satz des Backends. Mehrere Phrasen je Status, weil Python 3.13
 * vier davon nach RFC 9110 umbenannt hat und die Flotte in Produktion auf 3.11
 * läuft. `tests/test_error_detail_status_phrasen.py` hält die Tabelle gegen
 * `http.HTTPStatus` gleich.
 */
export const HTTP_STATUS_PHRASEN = {
    100: ['Continue'],
    101: ['Switching Protocols'],
    102: ['Processing'],
    103: ['Early Hints'],
    200: ['OK'],
    201: ['Created'],
    202: ['Accepted'],
    203: ['Non-Authoritative Information'],
    204: ['No Content'],
    205: ['Reset Content'],
    206: ['Partial Content'],
    207: ['Multi-Status'],
    208: ['Already Reported'],
    226: ['IM Used'],
    300: ['Multiple Choices'],
    301: ['Moved Permanently'],
    302: ['Found'],
    303: ['See Other'],
    304: ['Not Modified'],
    305: ['Use Proxy'],
    307: ['Temporary Redirect'],
    308: ['Permanent Redirect'],
    400: ['Bad Request'],
    401: ['Unauthorized'],
    402: ['Payment Required'],
    403: ['Forbidden'],
    404: ['Not Found'],
    405: ['Method Not Allowed'],
    406: ['Not Acceptable'],
    407: ['Proxy Authentication Required'],
    408: ['Request Timeout'],
    409: ['Conflict'],
    410: ['Gone'],
    411: ['Length Required'],
    412: ['Precondition Failed'],
    413: ['Request Entity Too Large', 'Content Too Large'],
    414: ['Request-URI Too Long', 'URI Too Long'],
    415: ['Unsupported Media Type'],
    416: ['Requested Range Not Satisfiable', 'Range Not Satisfiable'],
    417: ['Expectation Failed'],
    418: ["I'm a Teapot"],
    421: ['Misdirected Request'],
    422: ['Unprocessable Entity', 'Unprocessable Content'],
    423: ['Locked'],
    424: ['Failed Dependency'],
    425: ['Too Early'],
    426: ['Upgrade Required'],
    428: ['Precondition Required'],
    429: ['Too Many Requests'],
    431: ['Request Header Fields Too Large'],
    451: ['Unavailable For Legal Reasons'],
    500: ['Internal Server Error'],
    501: ['Not Implemented'],
    502: ['Bad Gateway'],
    503: ['Service Unavailable'],
    504: ['Gateway Timeout'],
    505: ['HTTP Version Not Supported'],
    506: ['Variant Also Negotiates'],
    507: ['Insufficient Storage'],
    508: ['Loop Detected'],
    510: ['Not Extended'],
    511: ['Network Authentication Required'],
};
/**
 * Ist `text` die Standard-Begründung von `status`? Ohne bekannten Status gibt
 * es nichts zu vergleichen — dann `false`, und der Text gilt als Prosa.
 */
export function istStatusPhrase(text, status) {
    if (status === undefined)
        return false;
    const phrasen = HTTP_STATUS_PHRASEN[status];
    return phrasen !== undefined && phrasen.includes(text.trim());
}
/** Den `detail`-Wert aus allem herausholen, was ein Fehler sein könnte. */
function rohesDetail(err) {
    if (err == null || typeof err !== 'object')
        return undefined;
    const o = err;
    // Axios: err.response.data.detail
    const response = o.response;
    if (response && typeof response === 'object') {
        const data = response.data;
        if (data && typeof data === 'object')
            return data.detail;
    }
    // Bereits geparster Body: { detail: … }
    if ('detail' in o)
        return o.detail;
    return undefined;
}
/**
 * Der Text, den man dem Nutzer zeigen darf — oder `null`.
 *
 * `null` heisst „nichts Anzeigbares dabei" — ein Maschinencode, oder die
 * englische Standard-Phrase des Status (`"Not Found"` bei einem 404, siehe
 * `HTTP_STATUS_PHRASEN`). Aufrufer hängen ihren eigenen
 * i18n-Text an: `extractErrorDetail(err) ?? t('errors.generic')`.
 */
export function extractErrorDetail(err) {
    const detail = rohesDetail(err);
    const status = errorStatus(err);
    if (typeof detail === 'string') {
        const text = detail.trim();
        if (!text)
            return null;
        return anzeigbar(text, status);
    }
    // Strukturiert: `message` ist für Menschen gedacht, `code` nicht.
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const o = detail;
        if (typeof o.message === 'string' && o.message.trim()) {
            return anzeigbar(o.message.trim(), status);
        }
        return null;
    }
    // Pydantic-Validierungsfehler kommen als Liste von Objekten. Die sind für
    // Entwickler, nicht für Nutzer — wer dem Nutzer trotzdem sagen will, WELCHES
    // Feld klemmt, nimmt `validierungsFelder`.
    return null;
}
/**
 * Bei einem 422: die Felder, die das Backend beanstandet hat.
 *
 * FastAPI antwortet auf einen Validierungsfehler mit einer Liste von Objekten
 * (`{type, loc, msg, input}`). Deren `msg` ist englischer Entwicklertext
 * („value is not a valid email address: The part after the @-sign is a
 * special-use or reserved name") und gehört nicht vor den Nutzer — deshalb
 * gibt `extractErrorDetail` dort `null` zurück.
 *
 * Das Ergebnis ist aber eine Sackgasse: Der Aufrufer zeigt „Speichern
 * fehlgeschlagen" und der Nutzer weiss nicht, welches der fünf Felder gemeint
 * ist. Genau so passiert am 13.09.2026 beim Anlegen eines Mandanten in
 * paperball-finance: die E-Mail-Domain war unzulässig, die Oberfläche sagte
 * nur „Anlegen fehlgeschlagen."
 *
 * Diese Funktion gibt deshalb die **Feldnamen** zurück — kein Text, keine
 * Übersetzung, nur `['email']`. Was daraus ein Satz wird, entscheidet die App
 * mit ihren eigenen Beschriftungen:
 *
 * ```ts
 * const felder = validierungsFelder(err)
 * setFehler(
 *   felder.length
 *     ? satzZuFeldern(felder.map(beschriftung)) // eigener i18n-Satz der App
 *     : extractErrorDetail(err) ?? t('errors.generic'),
 * )
 * ```
 *
 * `loc` beginnt bei FastAPI mit der Quelle (`body`, `query`, `path`); die
 * interessiert niemanden, also fällt sie weg. Verschachtelte Felder kommen
 * mit Punkt (`adresse.plz`), Listenindizes werden übersprungen.
 */
export function validierungsFelder(err) {
    const detail = rohesDetail(err);
    if (!Array.isArray(detail))
        return [];
    const felder = [];
    for (const eintrag of detail) {
        if (!eintrag || typeof eintrag !== 'object')
            continue;
        const loc = eintrag.loc;
        if (!Array.isArray(loc))
            continue;
        const teile = loc
            .filter((t) => typeof t === 'string')
            .filter((t, i) => !(i === 0 && (t === 'body' || t === 'query' || t === 'path' || t === 'header')));
        if (!teile.length)
            continue;
        const name = teile.join('.');
        if (!felder.includes(name))
            felder.push(name);
    }
    return felder;
}
/**
 * Der Code, auf den man verzweigen darf — oder `null`.
 *
 * Beispiel: `if (errorCode(err) === 'plan_missing') zeigeSperrkarte()`.
 * Bei `plan_limit_reached:seats:current=3:limit=2` kommt der **ganze** String
 * zurück; wer nur den Kopf braucht, nimmt `errorCodeHead`.
 */
export function errorCode(err) {
    const detail = rohesDetail(err);
    if (typeof detail === 'string') {
        const text = detail.trim();
        return istMaschinencode(text) ? text : null;
    }
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const code = detail.code;
        if (typeof code === 'string' && code.trim())
            return code.trim();
    }
    return null;
}
/** Nur der Teil vor dem ersten `:` — praktisch fuer `switch`. */
export function errorCodeHead(err) {
    const code = errorCode(err);
    return code === null ? null : code.split(':')[0];
}
/** HTTP-Status eines Fehlers, soweit erkennbar. */
export function errorStatus(err) {
    if (err == null || typeof err !== 'object')
        return undefined;
    const o = err;
    const response = o.response;
    if (response && typeof response.status === 'number')
        return response.status;
    if (typeof o.status === 'number')
        return o.status;
    return undefined;
}
/**
 * Trifft dieser Fehler eine Tarifgrenze? Nur dann gehoert die Sperrkarte hin.
 *
 * Die beiden Koepfe sind flottenweit gleich: die Merkmals-Gates melden
 * `plan_missing:<key>`, die Mengen-Gates
 * `plan_limit_reached:<key>:current=…:limit=…`.
 */
export function istTarifSperre(err) {
    const kopf = errorCodeHead(err);
    return kopf === 'plan_missing' || kopf === 'plan_limit_reached';
}
